import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { palette } from '$tablet-app/src/app/theme';
import { findAndUpdateRecord } from '$tablet-app/src/app/utils/arrays';
import { FeatureDomain, TaskListType } from '@akin-home/amplify';
import { Button } from '@akin-home/buttons/native';
import { CheckListItem, ChecklistModel } from '@akin-home/checklists';
import {
  useListTaskListsByHabitat,
  useUpdateTaskListItem,
} from '@akin-home/checklists/frontend';
import { useHabitatID } from '@akin-home/habitats/react';
import { Text } from '@akin-home/text/native';
import { colors, spacing } from '@akin-home/theme';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import styled from 'styled-components/native';
import { postUpdateRecordLogEvent } from '../../logging/functions';
import { useMainDrawerNavigation } from '../../navigation/hooks';
import { useCurrentUserID } from '../../users';
import DashboardPanel from '../DashboardPanel';
import Checklist from './Checklist';

interface ChecklistPanelProps {
  types: TaskListType[];
  small?: boolean;
}

const ChecklistPanel: FC<ChecklistPanelProps> = ({ types, small = false }) => {
  const [type, setType] = useState(types[0]);

  const user_id = useCurrentUserID();
  const habitatID = useHabitatID();

  const { navigate } = useMainDrawerNavigation();
  const openLists = () => navigate('Tasks');

  const { lists: checklists, isLoading } = useListTaskListsByHabitat({
    habitatID,
    filter: {
      or: types.map((t) => ({ type: { eq: t } })),
    },
  });

  const setChecklistItem = useUpdateTaskListItem();

  const onPress = useCallback(
    (checklist: ChecklistModel, item: CheckListItem) => {
      setChecklistItem({
        id: checklist.id,
        checklist: findAndUpdateRecord(checklist.checklist, item),
      });

      postUpdateRecordLogEvent({
        user_id,
        feature_domain: FeatureDomain.CHECKLISTS,
        data: {
          id: checklist.id,
          model: 'Checklist',
        },
      });
    },
    [setChecklistItem, user_id]
  );

  const currentChecklists = checklists.filter((c) => c.type === type);

  useEffect(() => {
    if ((types.length && !type) || !types.includes(type)) {
      setType(types[0]);
    }
  }, [type, types]);

  if (!isLoading && !checklists.length) {
    return (
      <PlaceholderPanel>
        <PlaceholderTextRow>
          <FontAwesomeIcon icon="tasks" size={25} color={colors.primary[70]} />
          <Text variant="h3" weight="semibold">
            Lists
          </Text>
        </PlaceholderTextRow>

        <Button
          size="large"
          icon="plus"
          variant="secondary"
          onPress={openLists}
        >
          Add your first list
        </Button>
      </PlaceholderPanel>
    );
  }

  return (
    <Panel>
      {types.length > 1 ? (
        <ScrollView
          horizontal
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContentContainer}
        >
          <TabContainer>
            {types.map((t) => (
              <Tab key={t} onPress={() => setType(t)} active={t === type}>
                <TabText
                  variant={small ? 'p2' : 'p'}
                  italic={false}
                  bold
                  color={
                    t === type ? palette.Primary_blue : 'rgba(5, 22, 51, 0.5)'
                  }
                >
                  {t}
                </TabText>
              </Tab>
            ))}
          </TabContainer>
        </ScrollView>
      ) : null}

      {isLoading ? (
        <FullScreenLoader />
      ) : (
        <Checklist
          checklist={currentChecklists}
          onPress={onPress}
          listEmptyComponent={
            <EmptyChecklistContainer>
              <Button
                size="medium"
                icon="plus"
                variant="secondary"
                onPress={openLists}
              >
                {`Add your first ${type.toLowerCase()} list`}
              </Button>
            </EmptyChecklistContainer>
          }
        />
      )}
    </Panel>
  );
};

const Panel = styled(DashboardPanel)`
  flex: 1;
  padding: 15px;
  gap: 10px;
`;

const EmptyChecklistContainer = styled(View)({
  width: '100%',
  flex: 1,
  gap: spacing[4],
  justifyContent: 'center',
  alignItems: 'center',
});

const TabContainer = styled.View`
  flex-direction: row;
  border-radius: 18px;
  padding: 5px;
  background-color: rgba(5, 22, 51, 0.1);
  min-width: 100%;
`;

const Tab = styled.TouchableOpacity<{ active?: boolean }>`
  padding: 10px 20px;
  border-radius: 18px;
  align-items: center;
  flex: 1;
  ${({ active }) => (active ? `background-color: white;` : undefined)};
`;

const TabText = styled(Text)`
  text-align: center;
`;

const PlaceholderPanel = styled(DashboardPanel)`
  background-color: #e5e6fd;
  flex: 1;
`;

const PlaceholderTextRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 14px;
`;

const styles = StyleSheet.create({
  scrollView: {
    minWidth: '100%',
    flexGrow: 0,
    flexShrink: 0,
  },
  scrollViewContentContainer: {
    minWidth: '100%',
  },
});

export default ChecklistPanel;
